import { FIANT_FAQ, FORTE_HOME } from '@constants/links';
import type { CSSProperties, FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeIn } from 'src/components/Animations';
import { useMediaQuery } from 'src/hooks/useMediaQuery';

const mdDownMediaQuery = '(max-width:900px)';

const styles: Record<string, CSSProperties> = {
  headerBase: { zIndex: 10 },
  headerDesktop: {
    position: 'absolute',
    left: 20,
    top: 20,
    textAlign: 'left'
  },
  headerMobile: {
    marginTop: 16,
    textAlign: 'center'
  },
  logoLink: {
    color: '#fff',
    textDecoration: 'none'
  },
  logoImg: {
    width: 100,
    height: 30,
    display: 'block'
  },
  content: {
    width: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    boxSizing: 'border-box'
  },
  contentDesktop: {
    position: 'absolute',
    top: -90
  },
  stack: {
    position: 'relative',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  illustration: {
    maxWidth: 570,
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  panel: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  panelDesktop: { bottom: -50 },
  panelMobile: { bottom: -120 },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: 'var(--novapay-on-accent)'
  },
  description: {
    margin: 0,
    marginBottom: 16,
    fontSize: 16,
    fontWeight: 400
  },
  descriptionTight: { marginTop: 8 },
  descriptionLoose: { marginTop: 24 },
  cta: {
    marginTop: 16,
    background: 'var(--novapay-on-accent)',
    padding: 10,
    width: 340,
    maxWidth: '100%',
    borderRadius: 24,
    border: 'none',
    color: '#333',
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'background 0.1s ease-in',
    fontFamily: 'inherit',
    fontSize: 14
  },
  ctaHover: {
    background: 'var(--novapay-on-accent-weak)'
  },
  faq: {
    margin: 0,
    marginTop: 24,
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--text-weak)'
  },
  faqLink: {
    color: 'var(--text-weak)',
    textDecoration: 'underline'
  }
};

const GenericResultView: FC<{
  isKycSubmitted?: boolean;
  isSuccess: boolean;
}> = ({ isKycSubmitted, isSuccess }) => {
  const { t } = useTranslation();
  const isSmallScreenSize = useMediaQuery(mdDownMediaQuery);
  const [ctaHovered, setCtaHovered] = useState(false);

  const getTitle = () => {
    if (!isKycSubmitted) {
      return t(`generic_result_page.${isSuccess ? 'success' : 'failure'}.title`);
    }

    return t('generic_result_page.success.title');
  };

  const getDescription = () => {
    if (!isKycSubmitted) {
      return t(`generic_result_page.${isSuccess ? 'success' : 'failure'}.description`);
    }

    return t('generic_result_page.kyc_submitted');
  };

  return (
    <FadeIn>
      <div
        style={{
          ...styles.headerBase,
          ...(isSmallScreenSize ? styles.headerMobile : styles.headerDesktop)
        }}>
        <a href={FORTE_HOME} rel="noopener noreferrer" style={styles.logoLink} target="_blank">
          <img alt="Forte" loading="eager" src="/images/logo_white.svg" style={styles.logoImg} />
        </a>
      </div>
      <div style={{ ...styles.content, ...(!isSmallScreenSize ? styles.contentDesktop : {}) }}>
        <div style={styles.stack}>
          <img
            alt=""
            src={`/images/generic_result_page/${isSuccess ? 'success' : 'failure'}.svg`}
            style={styles.illustration}
          />

          <div
            style={{
              ...styles.panel,
              ...(isSmallScreenSize ? styles.panelMobile : styles.panelDesktop)
            }}>
            <h2 style={styles.title}>{getTitle()}</h2>
            <p
              style={{
                ...styles.description,
                ...(isSmallScreenSize ? styles.descriptionLoose : styles.descriptionTight)
              }}>
              {getDescription()}
            </p>

            <button
              onClick={() => window.close()}
              onMouseEnter={() => setCtaHovered(true)}
              onMouseLeave={() => setCtaHovered(false)}
              style={{ ...styles.cta, ...(ctaHovered ? styles.ctaHover : {}) }}
              type="button">
              {t('generic_result_page.button_label')}
            </button>

            <p style={styles.faq}>
              {t('generic_result_page.faq_1')}
              <a href={FIANT_FAQ} rel="noopener noreferrer" style={styles.faqLink} target="_blank">
                &nbsp;{t('generic_result_page.faq_2')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

export default GenericResultView;
